import { useReveal } from './useReveal';

import type { ElementType, HTMLAttributes, ReactElement, ReactNode } from 'react';

/**
 * Wraps its children in a scroll-reveal container. Renders visible by default
 * (SSG/no-JS safe — see `useReveal`); after hydration, below-the-fold instances
 * fade + rise into view. Stagger sibling reveals with `delayMs` (cap the range
 * so a group never feels sluggish).
 *
 * Renders a `<div>` by default; pass `as` to preserve list/section semantics
 * (e.g. `as="li"` inside a `<ul>`).
 */
export interface RevealProps extends HTMLAttributes<HTMLElement> {
  readonly as?: ElementType;
  readonly delayMs?: number;
  readonly className?: string;
  readonly children: ReactNode;
}

export function Reveal({
  as: Tag = 'div',
  delayMs = 0,
  className,
  children,
  ...rest
}: RevealProps): ReactElement {
  const ref = useReveal<HTMLElement>(delayMs);
  return (
    <Tag ref={ref} className={['sx-reveal', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </Tag>
  );
}
